import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineAll } from '@datachain/ui-sdk/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	exhaustMap,
	map,
	switchMap,
	tap,
	withLatestFrom,
} from 'rxjs';

import { AjsStateService } from '../../ajs-state.service';
import { states } from '../../core/application.states';
import { SubscriptionRequester } from '../../requesters/subscription/subscription.requester';
import { PublicMetadataEntity } from '../domain/public-metadata.entity';
import { SubscriptionStatus } from '../domain/subscription.types';
import { ProjectSubscriptionAdapter } from '../project-subscription.adapter';
import { SubscriptionSimulatorSelector } from './subscription-simulator.selector';
import {
	fetchColumnModelsForSimulationFail,
	fetchColumnModelsForSimulationSuccess,
	fetchCurrentSubscriptionSimulationConfigFail,
	fetchCurrentSubscriptionSimulationConfigSuccess,
	fetchFilterModelsForSimulationFail,
	fetchFilterModelsForSimulationSuccess,
	fetchProfilesForSimulationFail,
	fetchProfilesForSimulationSuccess,
	fetchProfilesModelsForSimulationFail,
	fetchProfilesModelsForSimulationSuccess,
	fetchSubscriptionSimulationDataFail,
	fetchSubscriptionSimulationDataSuccess,
	getCurrentSubscriptionSimulationConfig,
	getProfilesModelsForSimulation,
	getSubscriptionSimulationData,
	navigateToSubscriptionSimulation,
	navigateToSubscriptionSimulationWithSelectedColumnModel,
	navigateToSubscriptionSimulationWithSelectedProfile,
	refreshColumnModelsForSimulation,
	refreshFilterModelsForSimulation,
	refreshProfilesForSimulation,
} from './subscription.actions';
import { SubscriptionSelector } from './subscription.selector';

@Injectable()
export class SubscriptionSimulatorEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################
	public navigateToSubscriptionSimulation$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					navigateToSubscriptionSimulation,
					navigateToSubscriptionSimulationWithSelectedProfile,
					navigateToSubscriptionSimulationWithSelectedColumnModel
				),
				tap((action) => {
					this.stateService.go(states.SubscriptionSimulator, {
						subscriptionId: `${action.subscriptionId}`,
					});
				})
			),
		{
			dispatch: false,
		}
	);
	// ##########################################################
	// Business Effects
	// ##########################################################

	public subscriptionSimulationConfig$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getCurrentSubscriptionSimulationConfig),
			withLatestFrom(this.subscriptionSelector.getPublicMetadata$()),
			switchMap(([action, currentSimulationMetadata]) =>
				this.subscriptionRequester.getSubscription(
					action.subscriptionId,
					currentSimulationMetadata.title !== ''
				)
			),
			map((subscription) =>
				fetchCurrentSubscriptionSimulationConfigSuccess({
					subscriptionId: subscription.id ? subscription.id : -1,
					subscriptionStatus: subscription.status
						? this.subscriptionAdapter.getSubscriptionStatus(
								subscription.status,
								subscription.history
						  )
						: SubscriptionStatus.Unknown,
					metadata: subscription.public_metadata
						? this.subscriptionAdapter.generatePublicMetadataEntity(
								subscription.public_metadata
						  )
						: PublicMetadataEntity.build(),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchCurrentSubscriptionSimulationConfigFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public initSimulation$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(getCurrentSubscriptionSimulationConfig),
				withLatestFrom(
					this.simulationSelector.getPreSelectedProfileForSimulation$(),
					this.simulationSelector.getPreSelectedColumnModelForSimulation$()
				),
				tap(([action, profileId, columnModelId]) => {
					if (profileId || columnModelId) {
						this.store.dispatch(
							getProfilesModelsForSimulation({
								subscriptionId: action.subscriptionId,
							})
						);
					}
				})
			),
		{
			dispatch: false,
		}
	);

	public getProfilesModelsForSimulation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getProfilesModelsForSimulation),
			switchMap((action) =>
				combineAll({
					subscriptionColumns:
						this.subscriptionRequester.getSubscriptionColumns(
							action.subscriptionId,
							false
						),
					profiles: this.subscriptionRequester.getSubscriptionProfiles(
						action.subscriptionId,
						false
					),
					columnModels: this.subscriptionRequester.getSubscriptionColumnModels(
						action.subscriptionId,
						false
					),
					filterModels: this.subscriptionRequester.getSubscriptionFilterModels(
						action.subscriptionId,
						false
					),
				})
			),
			map(({ subscriptionColumns, profiles, columnModels, filterModels }) => {
				const columns =
					this.subscriptionAdapter.generateSubscriptionColumns(
						subscriptionColumns
					);

				const profilesForSimulation =
					this.subscriptionAdapter.generateSubscriptionProfiles(
						profiles,
						columns
					);
				const generatedColumnModels =
					this.subscriptionAdapter.generateSubscriptionColumnModels(
						columnModels,
						columns,
						profilesForSimulation
					);
				const generatedFilterModels =
					this.subscriptionAdapter.generateSubscriptionFilterModels(
						filterModels,
						profilesForSimulation
					);
				return fetchProfilesModelsForSimulationSuccess({
					profiles: profilesForSimulation,
					columnModels: generatedColumnModels,
					filterModels: generatedFilterModels,
					subscriptionColumns: columns,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchProfilesModelsForSimulationFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshProfilesForSimulation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(refreshProfilesForSimulation),
			withLatestFrom(this.simulationSelector.getSimulationSubscriptionId$()),
			switchMap(([, subscriptionId]) =>
				combineAll({
					subscriptionColumns:
						this.subscriptionRequester.getSubscriptionColumns(subscriptionId),
					profiles: this.subscriptionRequester.getSubscriptionProfiles(
						subscriptionId,
						false
					),
				})
			),
			map(({ subscriptionColumns, profiles }) => {
				const generatedColumns =
					this.subscriptionAdapter.generateSubscriptionColumns(
						subscriptionColumns
					);
				const generatedProfile =
					this.subscriptionAdapter.generateSubscriptionProfiles(
						profiles,
						generatedColumns
					);
				return fetchProfilesForSimulationSuccess({
					profiles: generatedProfile,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchProfilesForSimulationFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshColumnModelsForSimulation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(refreshColumnModelsForSimulation),
			withLatestFrom(this.simulationSelector.getSimulationSubscriptionId$()),
			switchMap(([, subscriptionId]) =>
				combineAll({
					subscriptionColumns:
						this.subscriptionRequester.getSubscriptionColumns(subscriptionId),
					columnModels: this.subscriptionRequester.getSubscriptionColumnModels(
						subscriptionId,
						false
					),
				})
			),
			withLatestFrom(this.simulationSelector.getProfilesForSimulation$()),
			map(([{ subscriptionColumns, columnModels }, profiles]) => {
				const generatedColumns =
					this.subscriptionAdapter.generateSubscriptionColumns(
						subscriptionColumns
					);
				const generatedColumnModels =
					this.subscriptionAdapter.generateSubscriptionColumnModels(
						columnModels,
						generatedColumns,
						profiles
					);
				return fetchColumnModelsForSimulationSuccess({
					columnModels: generatedColumnModels,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchColumnModelsForSimulationFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshFilterModelsForSimulation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(refreshFilterModelsForSimulation),
			withLatestFrom(this.simulationSelector.getSimulationSubscriptionId$()),
			exhaustMap(([, subscriptionId]) =>
				this.subscriptionRequester.getSubscriptionFilterModels(
					subscriptionId,
					false
				)
			),
			withLatestFrom(this.simulationSelector.getProfilesForSimulation$()),
			map(([response, profiles]) => {
				const filterModels =
					this.subscriptionAdapter.generateSubscriptionFilterModels(
						response,
						profiles
					);
				return fetchFilterModelsForSimulationSuccess({
					filterModels,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchFilterModelsForSimulationFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getSubscriptionSimulationData$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getSubscriptionSimulationData),
			withLatestFrom(this.simulationSelector.getSimulationSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				this.subscriptionRequester.subscriptionSimulation(
					subscriptionId,
					action.dataSource,
					action.profile,
					action.displayColumns,
					action.sortColumns
				)
			),
			map((result) =>
				fetchSubscriptionSimulationDataSuccess({
					simulationData:
						this.subscriptionAdapter.generateSimulationData(result),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchSubscriptionSimulationDataFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public constructor(
		private readonly actions$: Actions,
		private readonly store: Store,
		private readonly subscriptionRequester: SubscriptionRequester,
		private readonly subscriptionSelector: SubscriptionSelector,
		private readonly simulationSelector: SubscriptionSimulatorSelector,
		private readonly subscriptionAdapter: ProjectSubscriptionAdapter,
		private readonly stateService: AjsStateService
	) {}
}
